/* oxlint-disable no-param-reassign -- https://redux-toolkit.js.org/usage/immer-reducers */
import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { z } from 'zod';
import { isCreditClaim } from '~/utils/claimLineItem';
import { ClaimType } from '~/utils/constants';
import {
  claimLineItemSchema,
  customerInfoSchema,
  shipmentMethodSchema,
  shipmentRateSchema,
  shopNowEventParamsSchema,
} from '~/utils/schemas';
import {
  completeCrewClaim,
  completeDraftClaimLineItem,
  invalidateLineItem,
  reset,
  setOrder,
} from './sharedActions';

// TODO: would like to separate into return and warranty
export const crewClaimStateSchema = z.nullable(
  z.object({
    status: z.enum(['new', 'submitted', 'completed']),
    orderIdFromPlatform: z.string(),
    claimType: z.enum(ClaimType),
    claimLineItems: z.array(claimLineItemSchema),
    customerInfo: z.nullable(customerInfoSchema),
    toCustomerRate: z.nullable(shipmentRateSchema),
    returnMethod: z.nullable(shipmentMethodSchema),
    shopNowIncentiveAmountApplied: z.number().optional(),
    offerShopNow: shopNowEventParamsSchema.optional(),
    shopNowId: z.string().optional(),
  }),
);

type CrewClaimState = z.infer<typeof crewClaimStateSchema>;

function assertInitialized(
  state: Draft<CrewClaimState>,
): asserts state is Draft<NonNullable<CrewClaimState>> {
  if (state === null) {
    throw new Error('Crew claim state is not initialized');
  }
}

export const crewClaimSlice = createSlice({
  name: 'crewClaim',
  initialState: null as CrewClaimState,
  reducers: {
    startClaim: (
      state,
      action: PayloadAction<
        Pick<NonNullable<CrewClaimState>, 'claimType' | 'orderIdFromPlatform'>
      >,
    ) => ({
      status: 'new',
      claimType: action.payload.claimType,
      orderIdFromPlatform: action.payload.orderIdFromPlatform,
      claimLineItems: [],
      customerInfo: null,
      toCustomerRate: null,
      returnMethod: null,
    }),
    setCustomerInfo: (
      state,
      action: PayloadAction<NonNullable<CrewClaimState>['customerInfo']>,
    ) => {
      assertInitialized(state);

      state.customerInfo = action.payload;
    },

    updateClaimLineItem: (
      state,
      action: PayloadAction<{
        id: string;
        updates: Partial<NonNullable<CrewClaimState>['claimLineItems'][number]>;
      }>,
    ) => {
      assertInitialized(state);

      const found = state.claimLineItems.find(
        (cli) => cli.id === action.payload.id,
      );

      if (found) {
        // found is an Immer draft, so we can mutate it directly
        Object.assign(found, action.payload.updates);
      }
    },

    removeClaimLineItem: (
      state,
      action: PayloadAction<
        NonNullable<CrewClaimState>['claimLineItems'][number]['id']
      >,
    ) => {
      assertInitialized(state);

      const index = state.claimLineItems.findIndex(
        (claim) => claim.id === action.payload,
      );

      if (index > -1) {
        state.claimLineItems.splice(index, 1);
      }
    },

    setReturnMethod: (
      state,
      action: PayloadAction<NonNullable<CrewClaimState>['returnMethod']>,
    ) => {
      assertInitialized(state);

      state.returnMethod = action.payload;
    },
    setOfferShopNow: (
      state,
      action: PayloadAction<NonNullable<CrewClaimState>['offerShopNow']>,
    ) => {
      assertInitialized(state);

      state.offerShopNow = action.payload;
    },

    setToCustomerRate: (
      state,
      action: PayloadAction<NonNullable<CrewClaimState>['toCustomerRate']>,
    ) => {
      assertInitialized(state);

      state.toCustomerRate = action.payload;
    },
    submitClaim: (state) => {
      assertInitialized(state);

      state.status = 'submitted';
    },
    unlockClaim: (state) => {
      assertInitialized(state);

      state.status = 'new';
    },
    clearClaim: () => null,
    setShopNowIncentiveAmountApplied: (
      state,
      action: PayloadAction<
        NonNullable<CrewClaimState>['shopNowIncentiveAmountApplied']
      >,
    ) => {
      assertInitialized(state);

      state.shopNowIncentiveAmountApplied = action.payload;
    },
    setShopNowId: (
      state,
      action: PayloadAction<NonNullable<CrewClaimState>['shopNowId']>,
    ) => {
      assertInitialized(state);
      state.shopNowId = action.payload;
    },
    setShopNowReplacementItems: (
      state,
      action: PayloadAction<
        NonNullable<CrewClaimState>['claimLineItems'][number]['replacementItems']
      >,
    ) => {
      assertInitialized(state);
      const replacementItems = action.payload;

      if (replacementItems) {
        const creditClaimLineItem = state.claimLineItems.find(isCreditClaim);

        if (creditClaimLineItem) {
          creditClaimLineItem.replacementItems = replacementItems;
        }
      }
    },
    clearShopNowResults: (state) => {
      assertInitialized(state);

      state.shopNowIncentiveAmountApplied = undefined;
      state.claimLineItems.forEach((item) => {
        item.replacementItems = undefined;
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(invalidateLineItem, (state, action) => {
        assertInitialized(state);

        state.claimLineItems = state.claimLineItems.filter(
          (claim) => claim.id !== action.payload,
        );
      })
      .addCase(completeDraftClaimLineItem, (state, action) => {
        assertInitialized(state);

        const index = state.claimLineItems.findIndex(
          (cli) => cli.id === action.payload.id,
        );

        if (index !== -1) {
          state.claimLineItems.splice(index, 1, action.payload);
        } else {
          state.claimLineItems.push(action.payload);
        }
      })
      .addCase(completeCrewClaim, (state) => {
        assertInitialized(state);

        state.status = 'completed';
      })
      // reset the claim state if the order is different than the one we started with
      // there are cases such as when a line item is invalidated and the order is re-fetched,
      // so we want to reset the claim state
      .addCase(setOrder, (state, action) =>
        action.payload.idFromPlatform === state?.orderIdFromPlatform
          ? state
          : null,
      )
      .addCase(reset, () => null);
  },
});
